/**
 * Crash-game math — a standalone leaf module (no hono/drizzle imports) so the client can
 * import the climb curve without pulling the server bundle. The HMAC seed-derivation of
 * the crash point lives in router.ts (it needs crypto.subtle + the secret); this module
 * is the pure, synchronous, testable math shared by client + server.
 *
 * The multiplier is stored on the leaderboard as an integer ×100 (2.47x → 247) because
 * scores.value is an integer column. dir: "desc" — higher cashout is better.
 */

/** Per-second exponential growth rate of the climb. The single "feel" knob. */
export const GROWTH = 0.18;

/** House edge baked into the crash curve (~1% of expected value). */
export const HOUSE_EDGE = 0.01;

/** Probability a round instant-busts at exactly 1.00x (the visible house edge). */
export const INSTANT_BUST_P = 0.01;

/** Leaderboard bounds for crash, ×100. 1.00x .. 1000.00x. */
export const CRASH_MIN = 100;
export const CRASH_MAX = 100_000;

/** Multiplier at `elapsedSec` into the round. m(0) = 1.00, accelerating. */
export function multiplierAt(elapsedSec: number): number {
  return Math.exp(GROWTH * Math.max(0, elapsedSec));
}

/** Seconds for the climb to reach multiplier `m` (inverse of multiplierAt). */
export function timeToReach(m: number): number {
  return Math.log(Math.max(1, m)) / GROWTH;
}

/**
 * The crash point from a uniform random r ∈ [0,1). Standard provably-fair crash curve:
 * P(crash ≥ X) ≈ (1 - HOUSE_EDGE)/X, plus an explicit instant-bust slice. Result is the
 * float multiplier (e.g. 4.20), clamped to the leaderboard ceiling.
 */
export function deriveCrashFromR(r: number): number {
  const u = Math.min(Math.max(r, 0), 0.999_999_999);
  if (u < INSTANT_BUST_P) return 1.0;
  // floor(99 / (1 - u)) / 100  — the bustabit/Stake formula with a 1% edge.
  const raw = Math.floor((100 * (1 - HOUSE_EDGE)) / (1 - u)) / 100;
  const capped = Math.min(Math.max(raw, 1.0), CRASH_MAX / 100);
  return capped;
}

/** Multiplier float → leaderboard integer (×100). 2.47x → 247. */
export function toScoreValue(mult: number): number {
  const v = Math.round(mult * 100);
  return Math.min(Math.max(v, CRASH_MIN), CRASH_MAX);
}

/** Leaderboard integer → display string. 247 → "2.47". */
export function fromScoreValue(value: number): string {
  return (value / 100).toFixed(2);
}
